import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="bag--payment-method"
export default class extends Controller {
    static targets = ["radio", "content", "reviewButton", "paypalButton", "affirmButton", "orderNotes"]

    connect() {
        //console.log('Bag Payment Method controller connected');
        // Initialize the display based on the checked radio button
        this.updateDisplay();
        
        // Prevent form submission when PayPal or Affirm is selected
        this.form = this.element.closest('form');
        this.boundSubmitHandler = this.handleFormSubmit.bind(this);
        this.boundPaymentMethodsHandler = this.applyAvailableMethods.bind(this);
        
        if (this.form) {
            // Use capture phase to run BEFORE other handlers
            this.form.addEventListener('submit', this.boundSubmitHandler, true);
        }
        document.addEventListener('bag:checkout:payment-methods-updated', this.boundPaymentMethodsHandler);
    }
    
    disconnect() {
        if (this.form && this.boundSubmitHandler) {
            this.form.removeEventListener('submit', this.boundSubmitHandler, true);
        }
        document.removeEventListener('bag:checkout:payment-methods-updated', this.boundPaymentMethodsHandler);
    }
    
    /**
     * Prevent form submission when Affirm is selected (Affirm has its own
     * popup-driven flow). PayPal now goes through Braintree which writes the
     * nonce into the order form, so PayPal submissions are allowed through.
     */
    handleFormSubmit(event) {
        const selectedRadio = this.radioTargets.find(radio => radio.checked);
        if (!selectedRadio) return true;

        const selectedValue = selectedRadio.value;

        if (selectedValue === 'AFFIRM') {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
        }

        return true;
    }

    /**
     * Called when a payment method radio button changes
     */
    switchMethod(event) {
        this.updateDisplay();
    }

    /**
     * Update the display of payment forms and buttons based on selected payment type
     */
    updateDisplay() {
        // Get the selected payment type
        const selectedRadio = this.radioTargets.find(radio => radio.checked);
        if (!selectedRadio) return;

        const selectedValue = selectedRadio.value;

        // Hide all payment method contents
        this.contentTargets.forEach(content => {
            content.style.display = 'none';
        });

        // Hide all payment buttons
        if (this.hasReviewButtonTarget) {
            this.reviewButtonTarget.style.display = 'none';
        }
        if (this.hasPaypalButtonTarget) {
            this.paypalButtonTarget.style.display = 'none';
        }
        if (this.hasAffirmButtonTarget) {
            this.affirmButtonTarget.style.display = 'none';
        }

        // Show the selected payment method content
        const contentId = 'pt-' + selectedValue.toLowerCase();
        const contentElement = document.getElementById(contentId);

        if (contentElement) {
            contentElement.style.display = 'block';
        }
        
        // Find the main submit button and disable it for PayPal/Affirm
        const submitButton = this.reviewButtonTarget?.querySelector('button[type="submit"]');

        // Show appropriate button based on payment type
        if (selectedValue === 'PP') {
            // PayPal is driven by the Braintree SDK button rendered inside
            // _payment_pp.html.erb; hide the "Place Order" button so the user
            // is funneled through PayPal explicitly.
            if (this.hasPaypalButtonTarget) {
                this.paypalButtonTarget.style.display = 'block';
            }
            if (this.hasOrderNotesTarget) {
                this.orderNotesTarget.style.display = 'block';
            }
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.style.display = 'none';
            }
        } else if (selectedValue === 'AFFIRM') {
            if (this.hasAffirmButtonTarget) {
                this.affirmButtonTarget.style.display = 'block';
            }
            // Hide order notes for Affirm
            if (this.hasOrderNotesTarget) {
                this.orderNotesTarget.style.display = 'none';
            }
            // Disable submit button to prevent Enter key submission
            if (submitButton) {
                submitButton.disabled = true;
                submitButton.style.display = 'none';
            }
        } else {
            if (this.hasReviewButtonTarget) {
                this.reviewButtonTarget.style.display = 'block';
            }
            // Show order notes for Credit Card and Bank Wire
            if (this.hasOrderNotesTarget) {
                this.orderNotesTarget.style.display = 'block';
            }
            // Re-enable submit button for CC and BW
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.style.display = '';
            }
        }
    }

    applyAvailableMethods(event) {
        const available = event.detail?.availablePaymentTypes || [];
        if (!available.length) return;

        let selectedStillAvailable = false;
        this.radioTargets.forEach((radio) => {
            const isAvailable = available.includes(radio.value);
            const card = radio.closest('.payment-method-card');
            if (card) card.style.display = isAvailable ? '' : 'none';
            radio.disabled = !isAvailable;
            if (radio.checked && isAvailable) selectedStillAvailable = true;
        });

        if (!selectedStillAvailable) {
            const fallback = this.radioTargets.find((radio) => !radio.disabled && radio.value === 'CC') ||
                this.radioTargets.find((radio) => !radio.disabled);
            if (fallback) {
                fallback.checked = true;
                fallback.dispatchEvent(new Event('change', {bubbles: true}));
            }
        }

        this.updateDisplay();
    }

    /**
     * Submit form for Affirm checkout
     */
    async submitAffirm(event) {
        event.preventDefault();
        
        // Find the form element
        const form = this.element.closest('form');
        if (!form) {
            console.error('Form not found');
            return;
        }

        // Validate the form first
        if (form._nativeValidator) {
            form._nativeValidator.submitButtonClicked = true;
            const isValid = form._nativeValidator.validateVisibleFields();
            form._nativeValidator.submitButtonClicked = false;
            
            if (!isValid) {
                //console.log('Form validation failed');
                return;
            }
        }

        // Show loading state
        const affirmButton = this.hasAffirmButtonTarget ? this.affirmButtonTarget : event.currentTarget;
        const originalHtml = affirmButton.innerHTML;
        affirmButton.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Processing...';
        affirmButton.style.pointerEvents = 'none';

        try {
            // Submit form data to backend
            const formData = new FormData(form);
            const csrfToken = document.querySelector('[name="csrf-token"]').content;
            
            const response = await fetch('/bag/affirm/checkout', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': csrfToken,
                    'Accept': 'application/json'
                },
                body: formData
            });

            const data = await response.json();
            
            if (!response.ok || !data.success) {
                const errorMessage = data.error || 'Failed to initialize Affirm checkout';
                console.error('Affirm checkout failed:', errorMessage);
                throw new Error(errorMessage);
            }

            //console.log('Affirm checkout data received:', data.checkout_data);

            // Find the Affirm controller and open checkout
            const affirmController = this.application.getControllerForElementAndIdentifier(
                document.querySelector('[data-controller*="affirm"]'),
                'affirm'
            );

            if (affirmController) {
                affirmController.openCheckout(data.checkout_data);
            } else {
                console.error('Affirm controller not found');
                alert('Unable to open Affirm checkout. Please try again.');
            }

        } catch (error) {
            console.error('Affirm checkout error:', error);
            alert(error.message || 'There was a problem initializing Affirm checkout. Please try again.');
        } finally {
            // Restore button state
            affirmButton.innerHTML = originalHtml;
            affirmButton.style.pointerEvents = '';
        }
    }
}
